import { isEqual } from "lodash";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { changeListParams, useDataProvider } from "react-admin";
import { useDispatch, useSelector } from "react-redux";
import { globalFilterKeys } from "./GridUserDataGlobalFiltersProvider";
import { isInArray } from "utils/general";

const GridUserDataContext = createContext({});

const checkAndCleanFilter = (filter = {}) => (
	Object.fromEntries(
		Object.entries(filter).filter(
			([key]) => !isInArray(globalFilterKeys, key)
		)
	)
);

const GridUserDataProvider = ({
	children,
	resource,
	filterDefaultValues,
	sortDefaultValues,
}) => {
	const dataProvider = useDataProvider();
	const dispatch = useDispatch();

	const resourceParamsState = useSelector((state) =>
		state.admin.resources[resource] ? state.admin.resources[resource].list.params : {}
	);

	const [isRestored, setIsRestored] = useState(false);
	const isInitialMount = useRef(true);
	const prevFilterRef = useRef(resourceParamsState.filter);
	const prevSortRef = useRef({
		order: resourceParamsState.order,
		sort: resourceParamsState.sort,
	});

	const keys = {
		filters: `${resource}-filters`,
		sort: `${resource}-sort`,
	};

	const updateUserData = useCallback((key, data) => {
		dataProvider.update("user-data", {
			id: key,
			data: { data },
		});
	}, [dataProvider]);

	const saveFilters = useCallback((filters) => {
		const cleanedFilter = checkAndCleanFilter(filters);
		if (!isEqual(cleanedFilter, prevFilterRef.current)) {
			updateUserData(keys.filters, filters);
			prevFilterRef.current = cleanedFilter;
		}
	}, [updateUserData, keys.filters]);

	const saveSort = useCallback((sort) => {
		const currentSort = {
			order: sort.order,
			sort: sort.field,
		};
		if (!isEqual(currentSort, prevSortRef.current)) {
			updateUserData(keys.sort, currentSort);
			prevSortRef.current = currentSort;
		}
	}, [updateUserData, keys.sort]);

	useEffect(() => {
		const restoreUserData = async () => {
			try {
				const [filtersResponse, sortResponse] = await Promise.all([
					dataProvider.getOne("user-data", { id: keys.filters }),
					dataProvider.getOne("user-data", { id: keys.sort }),
				]);

				const dispatchPayload = {
					...resourceParamsState,
					filter: checkAndCleanFilter({
						...filterDefaultValues,
						...filtersResponse.data?.data,
					}),
					order: sortResponse.data?.data?.order || sortDefaultValues.order,
					sort: sortResponse.data?.data?.sort || sortDefaultValues.field,
				};

				dispatch(changeListParams(resource, dispatchPayload));

				prevFilterRef.current = dispatchPayload.filter;
				prevSortRef.current = {
					order: dispatchPayload.order,
					sort: dispatchPayload.sort,
				};
			} finally {
				isInitialMount.current = false;
				setIsRestored(true);
			}
		};

		if (Object.keys(resourceParamsState).length !== 0 && isInitialMount.current) {
			restoreUserData();
		}
	}, [
		dataProvider,
		dispatch,
		filterDefaultValues,
		keys.filters,
		keys.sort,
		resource,
		sortDefaultValues,
	]);


	useEffect(() => {
		if (!isInitialMount.current) {
			const currentFilter = checkAndCleanFilter(resourceParamsState.filter);
			if (!isEqual(currentFilter, prevFilterRef.current)) {
				updateUserData(keys.filters, resourceParamsState.filter);
				prevFilterRef.current = currentFilter;
			}
		}
	}, [resourceParamsState.filter, updateUserData, keys.filters, isInitialMount]);

	useEffect(() => {
		if (!isInitialMount.current) {
			const currentSort = {
				order: resourceParamsState.order,
				sort: resourceParamsState.sort,
			};
			if (!isEqual(currentSort, prevSortRef.current)) {
				updateUserData(keys.sort, currentSort);
				prevSortRef.current = currentSort;
			}
		}
	}, [
		resourceParamsState.order,
		resourceParamsState.sort,
		updateUserData,
		keys.sort,
		isInitialMount,
	]);

    const getFilterDefaultValues = () => {
        return {
            ...filterDefaultValues,
        };
    };

    const getSortDefaultValues = () => {
        return {
            ...sortDefaultValues,
        };
    }
	// Block render until restoreUserData completes
	if (!isRestored) return null;
	
    if (!(resourceParamsState.order && resourceParamsState.sort)) return null;

    const contextValue = {
        filters: checkAndCleanFilter(resourceParamsState.filter),
        sort: {
            order: resourceParamsState.order,
            field: resourceParamsState.sort,
        },
        getFilterDefaultValues: getFilterDefaultValues,
        getSortDefaultValues: getSortDefaultValues,
		saveFilters,
		saveSort,
    };

	return (
		<GridUserDataContext.Provider value={contextValue}>
			{children}
		</GridUserDataContext.Provider>
	);
};

const useGridUserDataContext = () => {
	const context = useContext(GridUserDataContext);

	if (!context) {
		throw new Error("useGridUserDataContext must be used within a GridUserDataProvider");
	}

	return context;
};

export { GridUserDataProvider as default, useGridUserDataContext, GridUserDataContext };
